import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getMyTickets } from '../../services/feedbackService';
import { FeedbackTicketTable, useFeedbackViewMode, ViewModeToggle } from './feedbackShared';
import { FeedbackTicketCard } from './FeedbackTicketCard';
import { FeedbackFilters } from './FeedbackFilters';
import { useFeedbackFilters } from './useFeedbackFilters';
import type { FeedbackTicket } from '../../types/feedback';

export const MyFeedbackPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useFeedbackViewMode('my-feedback-view');

  const { state, update, clear, options, filtered, activeCount } = useFeedbackFilters(tickets);

  useEffect(() => {
    (async () => {
      try { setTickets(await getMyTickets()); }
      catch { /* */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

  const openTicket = (t: FeedbackTicket) => navigate(`/my-feedback/${t.FeedbackTicketKey}`);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.2rem', flex: 1 }}>
          My Feedback
        </Typography>
        {tickets.length > 0 && <ViewModeToggle value={view} onChange={setView} />}
      </Box>

      {tickets.length === 0 ? (
        <Typography color="text.secondary">You haven't submitted any feedback yet.</Typography>
      ) : (
        <>
          <FeedbackFilters
            state={state}
            update={update}
            clear={clear}
            options={options}
            activeCount={activeCount}
            total={tickets.length}
            shown={filtered.length}
          />

          {filtered.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
              No tickets match the current filters.
            </Typography>
          ) : view === 'cards' ? (
            <Box sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {filtered.map((t) => (
                <FeedbackTicketCard key={t.FeedbackTicketKey} ticket={t} onClick={() => openTicket(t)} />
              ))}
            </Box>
          ) : (
            <FeedbackTicketTable tickets={filtered} onRowClick={openTicket} />
          )}
        </>
      )}
    </Box>
  );
};

export default MyFeedbackPage;
